import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFloppyDisk,
  faPlus,
  faSpinner,
  faTags,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { hrToolsService } from '../../services/hrToolsService'
import {
  DEFAULT_ADVANCE_CATEGORY_DEFS,
  DEDUCTION_METHOD_OPTIONS,
  MAX_ADVANCE_CATEGORIES,
  MAX_ADVANCE_CATEGORY_LABEL_LENGTH,
  defaultDeductionMethod,
  type AdvanceCategoryDef,
  type DeductionMethod,
} from '../../utils/advanceCategories'

const HrAdvanceCategoriesCard: React.FC = () => {
  const [draft, setDraft] = useState<AdvanceCategoryDef[]>([...DEFAULT_ADVANCE_CATEGORY_DEFS])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const next = await hrToolsService.getAdvanceCategories()
        if (!cancelled) setDraft(next.length ? next : [...DEFAULT_ADVANCE_CATEGORY_DEFS])
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load transaction categories')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const patchAt = (index: number, patch: Partial<Pick<AdvanceCategoryDef, 'label' | 'deductionMethod'>>) => {
    setDraft((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)))
    setMessage(null)
  }

  const removeAt = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index))
    setMessage(null)
  }

  const addRow = () => {
    setDraft((prev) => (
      prev.length >= MAX_ADVANCE_CATEGORIES
        ? prev
        : [...prev, { key: '', label: '', deductionMethod: 'nextPayday' }]
    ))
    setMessage(null)
  }

  const onSave = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const next = await hrToolsService.saveAdvanceCategories(draft)
      setDraft(next)
      setMessage('Saved.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <FontAwesomeIcon icon={faTags} />
          Transaction categories
        </h2>
        <p className="text-sm text-base-content/70">
          These names appear on the Advances ledger and Profile. Assign how each category is
          deducted: EEAP takes $60 each payday (plus a catch-up if it is over $350); next payday
          takes the full balance on the next paycheck; first pay period of the month takes the
          full balance on that period&apos;s payday. Renaming keeps existing transactions. You cannot
          remove a category that still has transactions.
        </p>

        {error ? <div className="alert alert-error mt-2"><span>{error}</span></div> : null}
        {message ? <div className="alert alert-success mt-2"><span>{message}</span></div> : null}

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-base-content/70">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading transaction categories…
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {draft.length === 0 ? (
              <p className="text-sm text-base-content/60">Add at least one transaction category.</p>
            ) : (
              <div className="space-y-2">
                {draft.map((row, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
                      <div className="min-w-0 flex-1">
                        <input
                          className="input input-bordered w-full"
                          value={row.label}
                          maxLength={MAX_ADVANCE_CATEGORY_LABEL_LENGTH}
                          onChange={(e) => patchAt(index, { label: e.target.value })}
                          placeholder="Category name"
                          aria-label={`Transaction category ${index + 1}`}
                        />
                        {row.key ? (
                          <p className="mt-0.5 text-xs text-base-content/50">{row.key}</p>
                        ) : null}
                      </div>
                      <select
                        className="select select-bordered w-full sm:w-64 shrink-0"
                        value={row.deductionMethod || defaultDeductionMethod(row.key)}
                        onChange={(e) => patchAt(index, { deductionMethod: e.target.value as DeductionMethod })}
                        aria-label={`Deduction method for ${row.label || `category ${index + 1}`}`}
                      >
                        {DEDUCTION_METHOD_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-square text-error"
                      onClick={() => removeAt(index)}
                      aria-label="Remove"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn btn-outline"
                disabled={draft.length >= MAX_ADVANCE_CATEGORIES}
                onClick={addRow}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add category
              </button>
              <button
                type="button"
                className="btn btn-primary gap-2"
                disabled={saving}
                onClick={() => void onSave()}
              >
                <FontAwesomeIcon
                  icon={saving ? faSpinner : faFloppyDisk}
                  className={saving ? 'animate-spin' : ''}
                />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HrAdvanceCategoriesCard
